import React, { useEffect, useState, useRef } from "react";
import "./PlayCards.css";
import "../../DisplayItems/styles/CardView.css";
import { Skeleton, Tooltip } from "@mui/material";
import { editCard } from "../../../endpoints/FlashCards/functions";
import {
  Help,
  LocalLibraryRounded,
  CheckRounded,
  CloseRounded,
} from "@mui/icons-material";
import PlayNav from "./PlayNav";
import PlayFooter from "./PlayFooter";
import CardsReport from "../../Report/Cards/CardsReport";
import { EVALOPTIONS } from "../../../context/initialValues";

const PlayCards = (props) => {
  const [flip, setFlip] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileView, setMobileView] = useState(false);
  const [currentHintShown, setCurrentHintShown] = useState(false);

  const [isFinished, setIsFinished] = useState(false);
  const [hintCount, setHintCount] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [exitedStudyPileCount, setExitedStudyPileCount] = useState(0);
  const [cardHistory, setCardHistory] = useState([]);

  // Animation & Drag States
  const [swipeDirection, setSwipeDirection] = useState(null); // 'left', 'right', or null
  const [isAnimating, setIsAnimating] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const dragStart = useRef({ x: 0, y: 0 });
  // Tracks intent: null (undecided), "scroll" (vertical), or "swipe" (horizontal)
  const dragIntent = useRef(null);

  let card = props.playingCards[props.index];
  let nextCard = props.playingCards[props.index + 1];

  const handleClick = (event) => {
    event.stopPropagation();
    setOpen(!open);

    if (!currentHintShown) {
      setHintCount((prev) => prev + 1);
      setCurrentHintShown(true)
    }

    setTimeout(() => {
      setOpen(false);
    }, 5000);
  };

  useEffect(() => {
    const handleResize = () => {
      setMobileView(window.innerWidth < 950);
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // --- Drag & Gesture Handlers ---
  const handleDragStart = (e) => {
    if (isAnimating) return;
    setIsDragging(true);
    dragIntent.current = null; // Reset intent tracker
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragStart.current = {
      x: clientX,
      y: clientY,
    };
  };

  const handleDragMove = (e) => {
    if (!isDragging || isAnimating || dragIntent.current === "scroll") return;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const xOffset = clientX - dragStart.current.x;
    const yOffset = clientY - dragStart.current.y;

    // Detect user intent if not already locked
    if (!dragIntent.current) {
      const absX = Math.abs(xOffset);
      const absY = Math.abs(yOffset);

      // Require a small movement threshold to make a reliable decision
      if (absX > 5 || absY > 5) {
        if (absY > absX) {
          dragIntent.current = "scroll";
          setIsDragging(false); // Cancel card movement for vertical scrolls
          return;
        } else {
          dragIntent.current = "swipe";
        }
      } else {
        return; // Wait for clearer movement
      }
    }

    // Only move the card horizontally
    setDragOffset({ x: xOffset, y: 0 });

    // Live overlay hints while dragging
    if (xOffset < -50) setSwipeDirection("left");
    else if (xOffset > 50) setSwipeDirection("right");
    else setSwipeDirection(null);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    if (dragIntent.current === "swipe") {
      // If dragged far enough, trigger the action. Otherwise, snap back.
      if (dragOffset.x < -120) {
        animateAndTrigger("left", () => handleWrong(card.id));
      } else if (dragOffset.x > 120) {
        animateAndTrigger("right", () => handleCorrect(card.id));
      } else {
        setSwipeDirection(null);
        setDragOffset({ x: 0, y: 0 });
      }
    }
    dragIntent.current = null;
  };

  // Shared wrapper to trigger the full swipe visual before running logic
  const animateAndTrigger = (direction, actionNode) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setSwipeDirection(direction);

    // Fly off screen coordinates
    const flyX = direction === "left" ? -window.innerWidth : window.innerWidth;
    setDragOffset({ x: flyX, y: 0 });

    setTimeout(() => {
      actionNode();
    }, 300);
  };
  function updateCardHistory(wasCorrect, status, studyAmount) {
    setCardHistory((prev) => [
      ...prev,
      { question: card.question, wasCorrect, status, studyAmount },
    ]);
  }

  async function handleWrong(id) {
    setWrongCount((prev) => prev + 1);
    updateCardHistory(false, EVALOPTIONS.STILLSTUDYING, 3);

    let body = {
      deck_id: card.deck.id,
      question: card.question,
      answer: card.answer,
      hint: card.hint ? card.hint : "",
      in_study_pile: true,
      study_amount: 3,
    };
    try {
      await editCard(id, props.token, body);
    } catch (error) {
      console.log("Error occurred at handleWrong", error);
    }
    nextQuestion();
  }

  async function handleCorrect(id) {
    setCorrectCount((prev) => prev + 1);
    let status = EVALOPTIONS.ALWAYSRIGHT;

    let newAmount = 0
    if (card.in_study_pile) {
     newAmount = Math.max(0, card.study_amount - 1);
      if (newAmount === 0) {
        setExitedStudyPileCount((prev) => prev + 1);
        status = EVALOPTIONS.MASTERED;
      } else {
        status = EVALOPTIONS.ALMOSTTHERE;
      }
      let body = {
        deck_id: card.deck.id,
        question: card.question,
        answer: card.answer,
        hint: card.hint ? card.hint : "",
        in_study_pile: newAmount !== 0,
        study_amount: newAmount,
      };
      try {
        await editCard(id, props.token, body);
      } catch (error) {
        console.log("Error occurred at handleCorrect", error);
      }
    }
    updateCardHistory(true, status, newAmount);
    nextQuestion();
  }

  function nextQuestion() {
    if (props.index + 1 < props.playingCards.length) {
      props.setIndex(props.index + 1);
    } else {
      setIsFinished(true);
    }

    setSwipeDirection(null);
    setDragOffset({ x: 0, y: 0 });
    setIsAnimating(false);
    setIsDragging(false);
    setFlip(false);
    setOpen(false);
    setCurrentHintShown(false)
  }

  if (isFinished) {
    return (
      <CardsReport
        cardsTotal={props.playingCards.length}
        correctCount={correctCount}
        wrongCount={wrongCount}
        exitedStudyPileCount={exitedStudyPileCount}
        cardHistory={cardHistory}
        hintCount={hintCount}
      />
    );
  }

  const getCardStyle = () => {
    if (isDragging) {
      const tiltAngle = dragOffset.x * 0.04;
      return {
        transform: `translate3d(${dragOffset.x}px, ${dragOffset.y}px, 0) rotate(${tiltAngle}deg)`,
        transition: "none",
      };
    }
    if (isAnimating) {
      const tiltAngle = swipeDirection === "left" ? -14 : 14;
      return {
        transform: `translate3d(${dragOffset.x}px, ${dragOffset.y}px, 0) rotate(${tiltAngle}deg)`,
        transition: "transform 350ms cubic-bezier(0.16, 1, 0.3, 1)",
      };
    }
    return {};
  };

  const CardLoader = () => {
    const skeletonWidths = [
      "80%",
      "90%",
      "70%",
      "80%",
      "90%",
      "70%",
      "80%",
      "90%",
    ];

    const containerStyle = {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: "0.4rem",
    };

    return (
      <div className="flash-card-container">
        <div className="flash-card current-card" style={getCardStyle()}>
          <div className="card-front">
            <p style={containerStyle}>
              {skeletonWidths.map((width, index) => (
                <Skeleton
                  key={index}
                  variant="text"
                  sx={{ fontSize: "1rem", width }}
                  height={30}
                />
              ))}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div id="playing-mode" className="page">
      <PlayNav />

      {props.loading ? (
        <CardLoader />
      ) : (
        <div className="flash-card-container">
          {nextCard && (
            <div className="underlay-wrapper">
              <div
                className={`flash-card next-card-underlay ${isDragging || isAnimating ? "fade-in" : ""}`}
              >
                <div className="background"></div>
                <div className="card-front">
                  <p>{nextCard.question}</p>
                </div>
              </div>
            </div>
          )}

          <div
            key={props.index}
            className="flash-card current-card"
            style={getCardStyle()}
            onClick={() => !isDragging && !isAnimating && setFlip(!flip)}
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
            onMouseDown={handleDragStart}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
          >
            {swipeDirection === "left" && (
              <div className="card-overlay overlay-incorrect">
                <CloseRounded className="overlay-icon" color="error" />
              </div>
            )}
            {swipeDirection === "right" && (
              <div className="card-overlay overlay-correct">
                <CheckRounded className="overlay-icon" color="green" />
              </div>
            )}

            <div className="background">
              {card && card.in_study_pile && (
                <div className="study-icon">
                  <LocalLibraryRounded color="error" />
                </div>
              )}
              {card && card.hint && !flip ? (
                <Tooltip title={card ? card.hint : ""} open={open}>
                  <button onClick={handleClick} className="hint">
                    <Help
                      sx={{
                        color: open ? "inherit" : "#7d7d7d",
                        fontSize: "1.7rem",
                      }}
                    />
                  </button>
                </Tooltip>
              ) : null}
            </div>

            {!flip ? (
              <div className="card-front">
                <p>{card ? card.question : ""}</p>
              </div>
            ) : (
              <div className="card-back">
                <h3>{card ? card.answer : ""}</h3>
              </div>
            )}
          </div>
        </div>
      )}

      <PlayFooter
        loading={props.loading}
        handleWrong={() =>
          animateAndTrigger("left", () => handleWrong(card.id))
        }
        handleCorrect={() =>
          animateAndTrigger("right", () => handleCorrect(card.id))
        }
        mobileView={mobileView}
        card={card}
        cardIndex={props.index + 1}
        totalCards={props.total}
      />
    </div>
  );
};

export default PlayCards;
